// Pure heat-field math for the sentiment map (no DOM): a diverging sentiment
// ramp + inverse-distance-weighted interpolation over a pixel grid, with a
// coverage mask (alpha fades to 0 past `maxDist` from any data point, so
// regions with no data render blank). Lifted from the validated spike.

type Stop = [number, [number, number, number]];

// Cool ↔ hot diverging palette with two hues per side, so weak values stay a
// distinct visible color instead of desaturating into the paper: indigo (strong
// neg) → sky (weak neg) → paper (neutral) → gold (weak pos) → orange (strong pos).
// Still the colorblind-safe blue↔orange axis. Stops at ±0.15 keep the mild bands
// saturated, so only the true-neutral ±0.12 band reads as paper.
const LIGHT: Stop[] = [
  [-1, [70, 58, 147]], // #463a93 indigo
  [-0.15, [106, 168, 216]], // #6aa8d8 sky
  [0, [235, 227, 210]], // #ebe3d2 warm paper
  [0.15, [220, 168, 56]], // #dca838 gold
  [1, [223, 115, 39]], // #df7327 orange
];
// Dark: glows brighter on ink.
const DARK: Stop[] = [
  [-1, [148, 133, 232]], // #9485e8 indigo
  [-0.15, [127, 192, 238]], // #7fc0ee sky
  [0, [58, 53, 43]], // #3a352b dim
  [0.15, [232, 194, 90]], // #e8c25a gold
  [1, [237, 138, 68]], // #ed8a44 orange
];

/** Diverging sentiment color for v in [-1, 1] (clamped); interpolates between
 *  the bracketing stops, so any number of stops works. */
export function rampColor(v: number, dark: boolean): [number, number, number] {
  const s = dark ? DARK : LIGHT;
  const clamped = Math.max(-1, Math.min(1, v));
  let i = 0;
  while (i < s.length - 2 && clamped > s[i + 1][0]) i++;
  const a = s[i];
  const b = s[i + 1];
  const t = (clamped - a[0]) / (b[0] - a[0]);
  return [0, 1, 2].map((k) => Math.round(a[1][k] + (b[1][k] - a[1][k]) * t)) as [number, number, number];
}

export interface HeatPoint {
  x: number;
  y: number;
  v: number;
}

export interface IdwOptions {
  points: HeatPoint[];
  w: number;
  h: number;
  dark: boolean;
  power?: number;
  maxDist?: number;
  step?: number;
}

/**
 * IDW-interpolate `points` over a w×h grid → RGBA bytes. Alpha encodes
 * coverage: 1 at a data point, fading to 0 at `maxDist` px from the nearest —
 * so uncovered areas are transparent (blank). `step` trades fidelity for speed
 * (each computed pixel fills a step×step block).
 */
export function idwRaster({
  points,
  w,
  h,
  dark,
  power = 2.2,
  maxDist = 165,
  step = 2,
}: IdwOptions): Uint8ClampedArray {
  const data = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      let num = 0;
      let den = 0;
      let nearest = Infinity;
      for (const p of points) {
        const d = Math.hypot(x - p.x, y - p.y);
        if (d < nearest) nearest = d;
        const wt = 1 / (d + 1e-3) ** power;
        num += wt * p.v;
        den += wt;
      }
      const v = den ? num / den : 0;
      const cov = Math.max(0, 1 - nearest / maxDist);
      const [r, g, b] = rampColor(v, dark);
      const a = Math.round(Math.min(1, cov) * 235);
      for (let yy = 0; yy < step && y + yy < h; yy++) {
        for (let xx = 0; xx < step && x + xx < w; xx++) {
          const i = ((y + yy) * w + (x + xx)) * 4;
          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
          data[i + 3] = a;
        }
      }
    }
  }
  return data;
}
